import type { EntityJoin, ESQueryModel } from '@repo/api-codegen';
import { useApiGetEntityQueries } from '@repo/api-codegen';
import { useMemo } from 'react';
import type { IBaseModel } from '../../api';
import { FilterOptionType } from '../Filter';
import {
	AverageRunTimeRender,
	JoinOperatorRender,
	JoinTypeRender,
	LastExecutionRender,
	ReadOnlyQueryFrequentUsersRender,
	RenderWithIntegrationLogo,
	SQLRender,
	TotalRunsRender,
	TotalRunTimeRender,
} from '../TableV2/render';
import type { ExtendedDataTableColumn } from '../TableV2/types.ts';

interface QueryFilters {
	search_term?: string;
	filter?: unknown;
	sort?: string;
}

export const useQueryColumns = (): ExtendedDataTableColumn<
	ESQueryModel & IBaseModel
>[] =>
	useMemo(
		() => [
			{
				accessor: 'sql',
				titleLabel: 'SQL',
				render: SQLRender,
				sortable: false,
				explicit: false,
			},
			{
				accessor: 'frequent_users',
				titleLabel: 'Users',
				render: ReadOnlyQueryFrequentUsersRender,
				sortable: false,
				filterOptionType: FilterOptionType.OWNERS,
			},
			{
				accessor: 'average_runtime',
				titleLabel: 'Average runtime',
				render: AverageRunTimeRender,
				sortable: true,
				textAlignment: 'right',
			},
			{
				accessor: 'total_runtime',
				titleLabel: 'Total runtime',
				render: TotalRunTimeRender,
				sortable: true,
				textAlignment: 'right',
				filterOptionType: FilterOptionType.RUN_DATE,
			},
			{
				accessor: 'total_runs',
				titleLabel: 'Total runs',
				render: TotalRunsRender,
				sortable: true,
				textAlignment: 'right',
			},
			{
				accessor: 'last_execution_at',
				titleLabel: 'Last execution',
				render: LastExecutionRender,
				sortable: true,
			},
		],
		[]
	);

export const useJoinColumns = (): ExtendedDataTableColumn<EntityJoin>[] =>
	useMemo(
		() => [
			{
				accessor: 'join_type',
				titleLabel: 'Type',
				sortable: false,
				render: JoinTypeRender,
			},
			{
				accessor: 'left_table',
				titleLabel: 'Left table',
				render: (record: EntityJoin) =>
					RenderWithIntegrationLogo({
						textContent:
							record.left_table.title_cased || record.left_table.title,
						integrationType: record.left_table.integration_type || undefined,
						integrationId: record.left_table.integration_id,
					}),
			},
			{
				accessor: 'left_column',
				titleLabel: 'Left column',
				render: (record: EntityJoin) =>
					RenderWithIntegrationLogo({
						textContent:
							record.left_column.title_cased || record.left_column.title,
						integrationType: record.left_column.integration_type || undefined,
						integrationId: record.left_column.integration_id,
					}),
			},
			{
				accessor: 'operator',
				titleLabel: 'Operator',
				render: JoinOperatorRender,
				textAlignment: 'center',
			},
			{
				accessor: 'right_table',
				titleLabel: 'Right table',
				render: (record: EntityJoin) =>
					RenderWithIntegrationLogo({
						textContent:
							record.right_table.title_cased || record.right_table.title,
						integrationType: record.right_table.integration_type || undefined,
						integrationId: record.right_table.integration_id,
					}),
			},
			{
				accessor: 'right_column',
				titleLabel: 'Right column',
				render: (record: EntityJoin) =>
					RenderWithIntegrationLogo({
						textContent:
							record.right_column.title_cased || record.right_column.title,
						integrationType: record.right_column.integration_type || undefined,
						integrationId: record.right_column.integration_id,
					}),
			},
			// {
			// 	accessor: 'queries_count',
			// 	titleLabel: 'Queries count',
			// 	sortable: true,
			// 	textAlignment: 'right',
			// },
		],
		[]
	);

export const useActions = () => [];

export function useEntityQueryList(entityId: string, lookBackDays?: number) {
	return function useList({
		page = 1,
		filters = {} as QueryFilters,
		options = {},
	}) {
		return useApiGetEntityQueries(
			{
				pathParams: {
					entityId,
				},
				queryParams: {
					page,
					search_term: filters.search_term,
					filters: filters.filter ? JSON.stringify(filters.filter) : null,
					sort_by: filters.sort ? JSON.stringify(filters.sort) : '{}',
					page_size: 20,
					lookback_days: lookBackDays,
				},
			},
			{
				...options,
			}
		);
	};
}
